import { useEffect, useRef, useState } from "react";

/*
 * Avatar: a person's picture in the kit's round frame, or their initials on the dark disc when there is
 * no picture or it fails to load (owner 2026-10-13; the user menu, the portal and the dashboard's
 * Monogram each had their own). `color` is the person's own colour (a profile), passed to the stylesheet
 * at runtime as --avatar-color, always with `ink`, the colour that reads on it (black or white, the
 * contrast rule's readableOn), as --avatar-ink: a runtime colour cannot assume the kit's ink. Decorative by default: the name beside it carries the meaning; pass
 * `label` when the avatar stands alone. Styles in styles/data-display.css .avatar.
 */
type AvatarProps = {
    className?: string;
    name: string;
    src?: string | null;
    /** sm 32px, md 40px (the user menu, the default), lg 56px (a profile). */
    size?: "sm" | "md" | "lg";
    /** The dashboard's Monogram shape. */
    shape?: "round" | "square";
    /** An accessible name when the avatar stands without the name beside it. */
    label?: string;
} & ({ color?: undefined; ink?: undefined } | { color: string; ink: string });

/** Up to two initials; a number (a slot) is shown whole. */
export const initials = (name: string) =>
    /^\d+$/.test(name)
        ? name
        : name
              .split(/[\s@.·]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase())
              .join("");

const Avatar = ({ className, name, src, color, ink, size = "md", shape = "round", label }: AvatarProps) => {
    const [failed, setFailed] = useState(false);
    const picture = useRef<HTMLImageElement>(null);
    // An error that happened before hydration never reaches onError: check the image once mounted.
    useEffect(() => {
        const image = picture.current;
        if (image && image.complete && image.naturalWidth === 0) Promise.resolve().then(() => setFailed(true));
    }, [src]);
    const showPicture = Boolean(src) && !failed;
    return (
        <span
            className={`avatar ${size === "md" ? "" : `avatar--${size}`} ${shape === "square" ? "avatar--square" : ""} ${className || ""}`}
            style={color ? ({ "--avatar-color": color, ...(ink ? { "--avatar-ink": ink } : {}) } as React.CSSProperties) : undefined}
            role={label ? "img" : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
        >
            {showPicture ? <img ref={picture} src={src as string} alt="" onError={() => setFailed(true)} /> : initials(name) || "?"}
        </span>
    );
};

export default Avatar;
